import type { Joi } from '@ksyos/koi';
import { Map } from 'immutable';
import type { ISchemaContainer } from '@common/validation/schemaContainer';
import { megabytesToBytes } from '@common/utils/fileSize';
import { formatString } from '@common/utils/formatString';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from './getValidationErrorTranslation';

export type ValidationErrors = Map<string, string | undefined>;

export const emptyValidationErrors: ValidationErrors = Map();

export type IValidationResult<T> = IValidationValidResult<T> | IValidationInvalidResult;

interface IValidationValidResult<T> {
    errors: ValidationErrors;
    hasErrors: false;
    item: T;
}

interface IValidationInvalidResult {
    errors: ValidationErrors;
    hasErrors: true;
    item?: never;
}

const mapDetail = (map: { [key: string]: string }, detail: Joi.ValidationErrorItem) =>
    (map[detail.path.join('.')] = detail.type.replace('.', '_'));

export function validate<T>(schemaContainer: ISchemaContainer<T>, object: unknown): IValidationResult<T> {
    const result = schemaContainer.schema.validate(object, {
        abortEarly: false,
        convert: true,
        presence: 'required',
        stripUnknown: { arrays: false, objects: true },
    });
    if (result.error) {
        const map: { [key: string]: string } = {};
        for (const detail of result.error.details) {
            if (detail.type === 'alternatives.match' && detail.context && detail.context['details']) {
                const contextDetails = detail.context['details'];
                for (const contextDetail of contextDetails) {
                    mapDetail(map, contextDetail);
                }
            } else if (detail.type === 'object.and' && detail.context?.missing) {
                detail.path.push(detail.context.missing);
                mapDetail(map, detail);
            } else {
                mapDetail(map, detail);
            }
        }

        return { errors: Map(map), hasErrors: true };
    } else {
        return { errors: emptyValidationErrors, hasErrors: false, item: result.value };
    }
}

export function validateOne<TSchemaResult, TObject>(
    validationSchema: ISchemaContainer<TSchemaResult>,
    object: TObject,
    name: string,
    errorsStateObject: ValidationErrors,
    setErrorsStateObject: React.Dispatch<React.SetStateAction<ValidationErrors>>
) {
    setErrorsStateObject(updateValidationErrorsForOne(validationSchema, object, errorsStateObject, name));
}

export function updateValidationErrorsForOne<T>(
    schemaContainer: ISchemaContainer<T>,
    object: unknown,
    errors: ValidationErrors,
    name: string,
    errorKey?: string
): ValidationErrors {
    const { errors: newErrors } = validate(schemaContainer, object);
    if (newErrors.has(name)) {
        return errors.set(errorKey ? errorKey : name, newErrors.get(name));
    } else {
        return errors.remove(errorKey ? errorKey : name);
    }
}

export function updateValidationErrorsForMany<T>(
    schemaContainer: ISchemaContainer<T>,
    object: unknown,
    errors: ValidationErrors,
    names: string[],
    errorKey?: string
): ValidationErrors {
    const { errors: newErrors } = validate(schemaContainer, object);
    for (const name of names) {
        if (newErrors.has(name)) {
            errors = errors.set(errorKey ? errorKey : name, newErrors.get(name));
        } else {
            errors = errors.remove(errorKey ? errorKey : name);
        }
    }

    return errors;
}

export function errorsDeepCompareValue(validationErrors?: ValidationErrors) {
    return validationErrors
        ? validationErrors
              .entrySeq()
              .map((value) => value)
              .join(',')
        : undefined;
}

export function getErrorStartingWithKey(errors: ValidationErrors, errorKey: string): string | undefined {
    const foundError: string | undefined = errors.keySeq().find((key) => !!key?.startsWith(errorKey));

    return foundError ? getValidationErrorTranslation(errors.get(foundError)) : undefined;
}

export function validationErrorsToString(validationErrors: ValidationErrors) {
    return validationErrors.map((key, value) => `-${value}: ${key}`).join('\n');
}

export function validateFileSize<T extends string>(
    fieldName: T,
    file: File | null,
    options: { maxFileSizeInMegabytes: number }
): ValidationErrors {
    const translate = getSelectedLanguage();

    const maxFileSizeInBytes = megabytesToBytes(options.maxFileSizeInMegabytes);

    if (file && file.size > maxFileSizeInBytes) {
        return Map<T, string>().set(
            fieldName,
            formatString(translate.validation.excelParser.fileSizeMustBeLessThan, [
                options.maxFileSizeInMegabytes.toString(),
            ])
        );
    }

    return emptyValidationErrors;
}
